import FeatureFlagService from 'Services/FeatureFlags';
import {
  LoggerFactory, DOMAIN, FEATURE, type LoggerService,
} from 'Services/Logger';

const STAGE = {
  BOTH_TRACKERS: 1,
  NEW_TRACKER: 2,
  OLD_TRACKER: 0,
} as const;

type Tracker = () => Promise<unknown> | unknown;

type TFeatureFlagService = typeof FeatureFlagService;

const EMPTY_TRACKER: Tracker = () => undefined;

export class TrackerMediator {
  private oldTracker: Tracker = EMPTY_TRACKER;

  private newTracker: Tracker = EMPTY_TRACKER;

  private featureFlag: string;

  private featureFlagService: TFeatureFlagService = FeatureFlagService;

  private logger: LoggerService = LoggerFactory.getLogger(DOMAIN.FRONT);

  constructor(
    featureFlag: string,
    featureFlagService?: TFeatureFlagService,
    logger?: LoggerService,
  ) {
    this.featureFlag = featureFlag;
    if (featureFlagService) this.featureFlagService = featureFlagService;
    if (logger) this.logger = logger;
  }

  addOldTracker(tracker: Tracker): TrackerMediator {
    this.oldTracker = tracker;
    return this;
  }

  addNewTracker(tracker: Tracker): TrackerMediator {
    this.newTracker = tracker;
    return this;
  }

  private validate(): boolean {
    if (this.oldTracker === EMPTY_TRACKER) {
      this.logger.debug(
        'Old tracker is not set',
        `${DOMAIN.FRONT}.${FEATURE.FEATURE_FLAGS}`,
        { featureFlag: this.featureFlag },
      );
    }

    if (this.newTracker === EMPTY_TRACKER) {
      this.logger.debug(
        'New tracker is not set',
        `${DOMAIN.FRONT}.${FEATURE.FEATURE_FLAGS}`,
        { featureFlag: this.featureFlag },
      );
    }

    return true;
  }

  private getStage(): number {
    const value = this.featureFlagService.getFeatureFlag(this.featureFlag);
    if (value === undefined) return STAGE.OLD_TRACKER;
    if (!(typeof value === 'number')) return STAGE.OLD_TRACKER;
    return value;
  }

  private exec = async (stage: number): Promise<unknown> => {
    switch (stage) {
      case STAGE.OLD_TRACKER:
        return this.oldTracker();
      case STAGE.BOTH_TRACKERS:
        return Promise.allSettled([
          this.oldTracker(),
          this.newTracker(),
        ]);
      case STAGE.NEW_TRACKER:
        return this.newTracker();
      default:
        throw new Error('Invalid stage');
    }
  }

  async track() {
    const stage = this.getStage();
    try {
      this.validate();
      await this.exec(stage);
    } catch (error) {
      this.logger.warn(
        'Error tracking',
        `${DOMAIN.FRONT}.${FEATURE.FEATURE_FLAGS}`,
        { error, featureFlag: this.featureFlag, stage },
      );
    }
  }
}
